// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

export type OctoRule = components["schemas"]["OctoRulesResponse"];
export type OctoRuleDetail = components["schemas"]["OctoRulesDetailsResponse"];
export type OctoRuleStage = components["schemas"]["OctoRuleStageEnum"];
export type OctoRuleOutcome = components["schemas"]["OctoRuleOutcomeEnum"];
export type OctoRulesSortBy = components["schemas"]["OctoRulesSortByEnum"];

export interface OctoRulesResult {
  octoRules: OctoRule[];
  chains: components["schemas"]["ChainsRelevantResponse"][];
  stores: components["schemas"]["StoresRelevantResponse"][];
  count: number;
}

interface OctoRulesParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: OctoRulesSortBy;
  orderBy?: "asc" | "desc";
  chainName?: string;
  storeName?: string;
  stage?: OctoRuleStage;
  onlyActive?: boolean;
}

export function useOctoRulesQuery(params: OctoRulesParams = {}) {
  const { page = 1, limit = 20, search, sortBy, orderBy, chainName, storeName, stage, onlyActive } = params;

  return useQuery({
    queryKey: ["octo", "rules", { page, limit, search, sortBy, orderBy, chainName, storeName, stage, onlyActive }],
    queryFn: async () => {
      const res = await api.GET("/v1/octo/rules/", {
        params: {
          query: {
            page,
            limit,
            search: search || undefined,
            sortBy,
            orderBy,
            "filter[chainName]": chainName || undefined,
            "filter[storeName]": storeName || undefined,
            "filter[stage]": stage,
            "filter[onlyActive]": onlyActive,
          },
        },
      });
      return res.data as unknown as OctoRulesResult;
    },
  });
}

export function useOctoRuleQuery(octoRuleId: number) {
  return useQuery({
    queryKey: ["octo", "rule", octoRuleId],
    queryFn: async () => {
      const res = await api.GET("/v1/octo/rules/{octo_rule_id}", {
        params: { path: { octo_rule_id: octoRuleId } },
      });
      if (res.error) throw new Error("Failed to load octo rule");
      return res.data as unknown as OctoRuleDetail;
    },
    enabled: octoRuleId > 0,
  });
}

export function useToggleOctoRuleActiveMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { octoRuleId: number; isActive: boolean }) => {
      const res = await api.PATCH("/v1/octo/rules/{octo_rule_id}/active", {
        params: { path: { octo_rule_id: params.octoRuleId } },
        body: { isActive: params.isActive },
      });
      if (res.error) throw new Error("Failed to update octo rule status");
      return res.data as unknown as components["schemas"]["StatusResponse"];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["octo"] });
    },
  });
}

export function useCreateOctoRuleMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["OctoRulesCreateRequest"]) => {
      const res = await api.POST("/v1/octo/rules/", { body });
      if (res.error) throw new Error("Failed to create octo rule");
      return res.data as unknown as components["schemas"]["OctoRulesCreateResponse"];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["octo"] });
    },
  });
}

export function useUpdateOctoRuleMutation(octoRuleId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["OctoRulesEditRequest"]) => {
      const res = await api.PATCH("/v1/octo/rules/{octo_rule_id}", {
        params: { path: { octo_rule_id: octoRuleId } },
        body,
      });
      if (res.error) throw new Error("Failed to update octo rule");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["octo"] });
    },
  });
}
